// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Layers, View, Data and Help pages of the headset's side
 * panel (vrSidePanel), the in-headset counterparts of the browser's
 * Tools tabs (the AR refresh, 2026-10-04):
 *
 *   - Layers: the cards of Tools → Layers — Overlay (with its colour) and
 *     Real time (no Basemap since 2026-10-12: the Earth is the only one) —
 *     one open at a time, its choices in a list that scrolls (thumbstick,
 *     trigger drag or scroll bar), with the browser's limits (two overlays
 *     since 2026-10-09, the device's real-time cap). AYNI (owner, 2026-10-09): each playing stream's row has a grip
 *     (trigger on it and move the ray up or down to change the order they
 *     are drawn in; the top row is drawn on top) and a palette button,
 *     which opens that stream's colours on this page: the palettes, and
 *     − / + for its lower and upper limits (no keyboard to type them).
 *   - View: the palette, Invert and Reset. Typed colour limits stay in
 *     the browser. AYNI (owner, 2026-10-11): at its foot, the legend
 *     when its ✕ beside the globe has parked it here, with a button to
 *     put it back (or, while it is beside the globe, one to park it).
 *   - Data: pin mode (the trigger on the globe drops a pin), the pins with
 *     their values, the graph with the playhead, Mark highest & lowest
 *     and Clear all. Locked, with the reason, where the browser's are.
 *   - Help: what each control does.
 *
 * Painting only: the state comes from the host, the presses go back
 * through vrSidePanel as `VrPanelAction`s.
 */

import { t } from '../i18n'
import type { VrLayersState, VrPanelAction, VrPinsState, VrSatellitesState, VrViewState } from './vrSidePanel'
import type { VrLegend } from './vrLegend'
import { PANEL_COLORS, SCROLLBAR_W, clampScroll, type PanelPainter, type ScrollRegion } from './vrPanelKit'
import { PIN_NAME_MAX } from './measure/pins'
import { SURFACE_MODES, surfaceLabelKey } from './measure/surfaceMask'
import { formatTransparency } from './overlayDisplay'
import { xrExitButton } from '../utils/vrCapability'

const W = 1120
const H = 1000
const PAD = 40

/** Which Layers card is open, how far its list is scrolled (canvas pixels), and whose colours are open. */
export interface VrLayersView {
  section: 'overlay' | 'rt' | 'satellites' | null
  scroll: number
  /** AYNI: the playing stream whose colours fill the page, or null. */
  editing: string | null
}

export function initialLayersView(): VrLayersView {
  return { section: 'overlay', scroll: 0, editing: null }
}

/** The height of one choice in an open Layers card. */
export const LAYER_ROW_H = 66
/** A card's header, and the gap under it. */
const SECTION_HEAD_H = 104
/** AYNI: the width of a playing stream's eye and ✕ on the Layers page. */
const RT_ACTION_W = 84
const SECTION_GAP = 12

function header(p: PanelPainter<VrPanelAction>, title: string): void {
  p.frame(32)
  p.text(title, PAD, 70, { size: 42, weight: 700 })
  p.button(W - PAD - 68, 36, 68, 68, '', { kind: 'close' }, { icon: 'close', radius: 34 })
}

function note(p: PanelPainter<VrPanelAction>, text: string, y: number): void {
  const lines = p.wrap(text, W - PAD * 2, 4, 28)
  lines.forEach((line, i) => p.text(line, PAD, y + i * 40, { size: 28, color: PANEL_COLORS.textSecondary }))
}

// --- Layers -----------------------------------------------------------

const SECTION_COLOR = { overlay: '#fca5a5', rt: '#67e8f9', satellites: '#3fe6ff' } as const
/** A stream kind's pill colour, as Browse's badges: live cyan, forecast amber, projection yellow; the rest grey. */
const KIND_COLOR: Record<string, string> = { 'real-time': SECTION_COLOR.rt, forecast: PANEL_COLORS.warning, projection: '#facc15' }

/** Draw the Layers page; returns the open card's scroll region, if it has one. */
/** AYNI (satellites plan): the Satellites section's rows of switches. */
function drawSatellites(p: PanelPainter<VrPanelAction>, s: VrSatellitesState, y0: number): number {
  const rowH = 60
  const gap = 12
  const labelW = 190
  let y = y0
  const row = (label: string, items: Array<{ text: string; action: VrPanelAction; active: boolean; disabled?: boolean }>) => {
    p.text(label, PAD + 16, y + rowH / 2, { size: 22, weight: 650, color: PANEL_COLORS.textSecondary, maxWidth: labelW - 16 })
    const x0 = PAD + labelW
    const w = (W - PAD - x0 - (items.length - 1) * 10) / items.length
    items.forEach((it, i) => {
      const x = x0 + i * (w + 10)
      if (it.disabled) {
        p.box(x, y, w, rowH, 16, 'rgba(255,255,255,0.03)', 'rgba(255,255,255,0.08)', 1.5)
        p.text(it.text, x + w / 2, y + rowH / 2, { size: 22, color: PANEL_COLORS.muted, align: 'center', maxWidth: w - 12 })
      } else {
        p.button(x, y, w, rowH, it.text, it.action, { active: it.active, size: 22 })
      }
    })
    y += rowH + gap
  }
  const top: Array<{ text: string; action: VrPanelAction; active: boolean; disabled?: boolean }> = [
    { text: s.on ? t('satellites.on') : t('satellites.off'), action: { kind: 'sat', op: 'on' }, active: s.on, disabled: !s.onEarth },
  ]
  if (s.skyAvailable) top.push({ text: t('satellites.sky.button'), action: { kind: 'sat', op: 'sky' }, active: s.sky })
  if (s.sky) top.push({ text: t('satellites.sky.setNorth'), action: { kind: 'sat', op: 'north' }, active: false })
  // AYNI (owner, 2026-11-03): Fully VR is its own view (space), beside Sky above me, not inside it.
  if (s.skyAvailable) top.push({ text: t('satellites.sky.fullVr'), action: { kind: 'sat', op: 'fullVr' }, active: s.fullVr })
  row(t('satellites.title'), top)
  // Another body on the globe (owner, 2026-10-23): the switch is held and the section says why.
  if (!s.onEarth) {
    p.text(s.status, PAD + 16, y + 18, { size: 21, color: PANEL_COLORS.muted, maxWidth: W - PAD * 2 - 32 })
    return y + 12 + 32
  }
  if (!s.on) return y
  const debris = s.show.some((v) => v.endsWith('-debris'))
  row(t('satellites.show.label'), [
    { text: t('satellites.show.highlights'), action: { kind: 'sat-show', set: 'highlights' }, active: s.show.includes('highlights') },
    { text: t('satellites.show.all'), action: { kind: 'sat-show', set: 'all' }, active: s.show.includes('all') },
    { text: t('satellites.show.starlink'), action: { kind: 'sat-show', set: 'starlink' }, active: s.show.includes('starlink') },
    { text: t('satellites.show.withDatasets'), action: { kind: 'sat-show', set: 'with-datasets' }, active: s.show.includes('with-datasets') },
    { text: t('satellites.category.debris'), action: { kind: 'sat', op: 'debris' }, active: debris },
  ])
  row(t('satellites.colourBy.label'), [
    { text: t('satellites.colourBy.orbit'), action: { kind: 'sat-colour', by: 'orbit' }, active: s.colourBy === 'orbit' },
    { text: t('satellites.colourBy.country'), action: { kind: 'sat-colour', by: 'country' }, active: s.colourBy === 'country', disabled: !s.countryReady },
    { text: t('satellites.colourBy.purpose'), action: { kind: 'sat-colour', by: 'purpose' }, active: s.colourBy === 'purpose' },
  ])
  row(t('satellites.height.short'), [
    { text: t('satellites.height.true'), action: { kind: 'sat-altitude', scale: 'true' }, active: s.altitude === 'true' },
    { text: t('satellites.height.compact'), action: { kind: 'sat-altitude', scale: 'compact' }, active: s.altitude === 'compact' },
  ])
  const speeds: Array<{ text: string; action: VrPanelAction; active: boolean }> = [
    { text: t('satellites.time.live'), action: { kind: 'sat', op: 'live' }, active: s.live },
  ]
  for (const v of [60, 300, 1200]) speeds.push({ text: v + '×', action: { kind: 'sat-speed', speed: v }, active: !s.live && s.speed === v })
  speeds.push({ text: t('satellites.trails'), action: { kind: 'sat', op: 'trails' }, active: s.trails })
  row(t('satellites.time.speed'), speeds)
  // Under the switches: the sky's second line (the ISS's next pass), and the stale note.
  const lines = [s.detail, s.stale ? t('satellites.status.stale') : ''].filter(Boolean) as string[]
  lines.forEach((line, i) => p.text(line, PAD + 16, y + 18 + i * 32, { size: 21, color: i === lines.length - 1 && s.stale ? PANEL_COLORS.warning : PANEL_COLORS.muted, maxWidth: W - PAD * 2 - 32 }))
  y += 12 + lines.length * 32
  // Sky above me: the catalog's satellites passing over, each with its dataset to load.
  for (const pass of s.passes ?? []) {
    const btnW = 150
    p.text(pass.text, PAD + 16, y + rowH / 2, { size: 21, color: PANEL_COLORS.textSecondary, maxWidth: W - PAD * 2 - btnW - 40 })
    if (pass.datasetId) p.button(W - PAD - btnW, y, btnW, rowH, t('satellites.card.load'), { kind: 'browse-select', id: pass.datasetId, locked: null }, { size: 22 })
    y += rowH + gap
  }
  return y
}

export function drawLayers(p: PanelPainter<VrPanelAction>, state: VrLayersState | null, view: VrLayersView): ScrollRegion | null {
  header(p, t('vr.layers.title'))
  if (!state) {
    note(p, t('vr.layers.none'), 150)
    return null
  }
  // AYNI: a stream's colours and transparency, while open (and while it still plays).
  const editing = view.editing ? state.rtPlaying.find((rt) => rt.id === view.editing) : undefined
  if (editing) {
    drawRtColours(p, editing)
    return null
  }
  const sections: Array<{
    id: 'overlay' | 'rt' | 'satellites'
    title: string
    limit: string
    current: string
    options: Array<{ id: string | null; title: string; selected: boolean; kind?: string; kindId?: string }>
  }> = [
    {
      id: 'overlay',
      title: t('tools.layers.overlay'),
      limit: t('vr.layers.upTo', { count: state.overlayCap }),
      current: state.overlaysOn.length ? state.overlaysOn.map((o) => o.title).join(', ') : t('tools.layers.overlay.none'),
      options: [{ id: null, title: t('tools.layers.overlay.none'), selected: state.overlaysOn.length === 0 },
        ...state.overlays.map((o) => ({ id: o.id, title: o.title, selected: state.overlaysOn.some((on) => on.id === o.id) }))],
    },
    {
      id: 'rt',
      title: t('tools.layers.rt'),
      limit: t('vr.layers.upTo', { count: state.rtCap }),
      current: state.rtSelected.length
        ? state.rt.filter((r) => state.rtSelected.includes(r.id)).map((r) => r.title).join(', ') || t('tools.layers.rt.none')
        : t('tools.layers.rt.none'),
      options: [{ id: null, title: t('tools.layers.rt.none'), selected: state.rtSelected.length === 0 },
        ...state.rt.map((r) => ({ id: r.id, title: r.title, selected: state.rtSelected.includes(r.id), kind: r.kind, kindId: r.kindId }))],
    },
  ]
  // AYNI (satellites plan): the Satellites section, under the two layer cards.
  const sats = state.satellites
  if (sats) {
    sections.push({
      id: 'satellites',
      title: t('satellites.title'),
      limit: '',
      current: sats.on || !sats.onEarth ? sats.status : t('satellites.off'),
      options: [],
    })
  }

  let y = 130
  let region: ScrollRegion | null = null
  sections.forEach((s, index) => {
    const open = view.section === s.id
    p.box(PAD, y, W - PAD * 2, SECTION_HEAD_H, 20, open ? 'rgba(255,255,255,0.08)' : PANEL_COLORS.surface, open ? SECTION_COLOR[s.id] : PANEL_COLORS.border, open ? 2.5 : 1.5)
    p.text(s.title.toUpperCase(), PAD + 28, y + 34, { size: 22, weight: 800, color: SECTION_COLOR[s.id] })
    p.text(s.limit, W - PAD - 92, y + 34, { size: 22, color: PANEL_COLORS.muted, align: 'right' })
    p.text(s.current, PAD + 28, y + 74, { size: 28, weight: 600, maxWidth: W - PAD * 2 - 150 })
    p.icon(open ? 'chevron-left' : 'chevron-right', W - PAD - 44, y + SECTION_HEAD_H / 2, 30, PANEL_COLORS.textSecondary)
    p.hit(PAD, y, W - PAD * 2, SECTION_HEAD_H, { kind: 'layer-section', section: s.id })
    y += SECTION_HEAD_H + SECTION_GAP
    if (!open) return
    if (s.id === 'satellites') {
      if (sats) y = drawSatellites(p, sats, y)
      return
    }

    // AYNI (2026-10-09): a colour row for each overlay on; named when there are two.
    if (s.id === 'overlay' && state.overlaysOn.length) {
      const tints = [
        { tint: 'white' as const, label: t('tools.layers.tint.white') },
        { tint: 'black' as const, label: t('tools.layers.tint.black') },
        { tint: 'source' as const, label: t('tools.layers.tint.source') },
      ]
      const named = state.overlaysOn.length > 1
      const nameW = named ? 300 : 0
      const x0 = PAD + (named ? nameW + 16 : 0)
      const tw = (W - PAD - x0 - 24) / 3
      for (const on of state.overlaysOn) {
        if (named) p.text(on.title, PAD, y + 32, { size: 22, weight: 650, color: PANEL_COLORS.textSecondary, maxWidth: nameW })
        tints.forEach((c, i) => {
          p.button(x0 + i * (tw + 12), y, tw, 64, c.label, { kind: 'layer-tint', id: on.id, tint: c.tint }, { active: on.tint === c.tint, size: named ? 22 : 24 })
        })
        y += 78
      }
    }
    // AYNI: the streams playing now, each with its eye and its ✕, above
    // the list (where a playing stream may be far down), as the 2D date
    // tracks carry them beside each overlay's name.
    if (s.id === 'rt' && state.rtPlaying.length) {
      const rowH = LAYER_ROW_H - 8
      const x = PAD + 16
      const w = W - PAD * 2 - 32
      // AYNI (2026-10-09): top first, as the browser's tracks: the top one is drawn on top.
      const rows = [...state.rtPlaying].reverse()
      for (const rt of rows) {
        const cy = y + rowH / 2
        p.box(x, y, w, rowH, 14, 'rgba(250,204,21,0.08)', 'rgba(250,204,21,0.45)', 2)
        // The grip (a drag from it moves the stream) and the palette button.
        const movable = rows.length > 1
        p.icon('grip', x + RT_ACTION_W / 2, cy, 30, movable ? PANEL_COLORS.textSecondary : 'rgba(255,255,255,0.2)')
        if (movable) p.hit(x, y, RT_ACTION_W, rowH, { kind: 'layer-rt-grip', id: rt.id })
        const px = x + RT_ACTION_W
        p.box(px, y + 8, 1.5, rowH - 16, 0, 'rgba(250,204,21,0.35)')
        const sx = px + RT_ACTION_W / 2 - 9
        if (rt.colours && rt.colours.ramp.length > 1) {
          const grad = p.g.createLinearGradient(0, y + rowH - 10, 0, y + 10)
          rt.colours.ramp.forEach((c, k) => grad.addColorStop(k / (rt.colours!.ramp.length - 1), c))
          p.roundPath(sx, y + 10, 18, rowH - 20, 4)
          p.g.fillStyle = grad
          p.g.fill()
        } else {
          p.box(sx, y + 10, 18, rowH - 20, 4, 'rgba(255,255,255,0.06)', 'rgba(255,255,255,0.18)', 1.5)
        }
        // AYNI (owner, 2026-10-12): it opens for every stream, for the transparency at least.
        p.hit(px, y, RT_ACTION_W, rowH, { kind: 'layer-rt-colours', id: rt.id })
        const tx = x + 2 * RT_ACTION_W + 16
        p.text(rt.title, tx, cy, { size: 26, weight: 650, color: rt.hidden ? PANEL_COLORS.muted : PANEL_COLORS.text, maxWidth: w - (tx - x) - 2 * RT_ACTION_W - 16 })
        const eyeX = x + w - 2 * RT_ACTION_W
        const closeX = x + w - RT_ACTION_W
        p.icon(rt.hidden ? 'eye-off' : 'eye', eyeX + RT_ACTION_W / 2, cy, 32, rt.hidden ? '#facc15' : PANEL_COLORS.textSecondary)
        p.icon('close', closeX + RT_ACTION_W / 2, cy, 28, PANEL_COLORS.textSecondary)
        p.hit(eyeX, y, RT_ACTION_W, rowH, { kind: 'layer-rt-hidden', id: rt.id, hidden: !rt.hidden })
        p.hit(closeX, y, RT_ACTION_W, rowH, { kind: 'layer-rt', id: rt.id })
        y += LAYER_ROW_H
      }
      if (rows.length > 1) {
        p.text(t('vr.layers.rtOrder'), x, y + 12, { size: 21, color: PANEL_COLORS.muted, maxWidth: w })
        y += 34
      }
      y += 8
    }
    // AYNI (owner, 2026-10-12): the dataset below the streams, as the browser's green track:
    // its lock, its name, its eye and its ✕ (it and every overlay off).
    if (s.id === 'rt' && state.base) {
      const base = state.base
      const rowH = LAYER_ROW_H - 8
      const x = PAD + 16
      const w = W - PAD * 2 - 32
      const cy = y + rowH / 2
      p.box(x, y, w, rowH, 14, 'rgba(34,197,94,0.08)', 'rgba(34,197,94,0.45)', 2)
      p.icon('lock', x + RT_ACTION_W / 2, cy, 24, 'rgba(255,255,255,0.35)')
      // AYNI (owner, 2026-10-13): its palette button, as the streams have theirs: its colours and transparency (View).
      const px = x + RT_ACTION_W
      p.box(px, y + 8, 1.5, rowH - 16, 0, 'rgba(34,197,94,0.35)')
      const sx = px + RT_ACTION_W / 2 - 9
      if (base.ramp && base.ramp.length > 1) {
        const grad = p.g.createLinearGradient(0, y + rowH - 10, 0, y + 10)
        base.ramp.forEach((c, k) => grad.addColorStop(k / (base.ramp!.length - 1), c))
        p.roundPath(sx, y + 10, 18, rowH - 20, 4)
        p.g.fillStyle = grad
        p.g.fill()
      } else {
        p.box(sx, y + 10, 18, rowH - 20, 4, 'rgba(255,255,255,0.06)', 'rgba(255,255,255,0.18)', 1.5)
      }
      p.hit(px, y, RT_ACTION_W, rowH, { kind: 'base-colours' })
      const tx = x + 2 * RT_ACTION_W + 16
      p.text(base.title, tx, cy, { size: 26, weight: 650, color: base.hidden ? PANEL_COLORS.muted : PANEL_COLORS.text, maxWidth: w - (tx - x) - 2 * RT_ACTION_W - 16 })
      const eyeX = x + w - 2 * RT_ACTION_W
      const closeX = x + w - RT_ACTION_W
      p.icon(base.hidden ? 'eye-off' : 'eye', eyeX + RT_ACTION_W / 2, cy, 32, base.hidden ? PANEL_COLORS.accent : PANEL_COLORS.textSecondary)
      p.icon('close', closeX + RT_ACTION_W / 2, cy, 28, PANEL_COLORS.textSecondary)
      p.hit(eyeX, y, RT_ACTION_W, rowH, { kind: 'base-hidden', hidden: !base.hidden })
      p.hit(closeX, y, RT_ACTION_W, rowH, { kind: 'base-close' })
      y += LAYER_ROW_H + 8
    }
    // The list gets the room the cards under it leave on the panel.
    const below = (sections.length - index - 1) * (SECTION_HEAD_H + SECTION_GAP)
    const note2 = s.id === 'rt' && state.rtCap > 1 && state.rtSelected.length > 1 ? 44 : 0
    const room = H - 36 - y - below - note2
    const content = s.options.length * LAYER_ROW_H
    const listRegion: ScrollRegion = { x: PAD + 16, y, w: W - PAD * 2 - 32, h: Math.max(LAYER_ROW_H, Math.min(content, room)), content }
    const scroll = clampScroll(view.scroll, listRegion)
    const rowW = listRegion.w - (content > listRegion.h ? SCROLLBAR_W + 12 : 0)
    p.clip(listRegion.x, listRegion.y, listRegion.w, listRegion.h)
    s.options.forEach((o, i) => {
      const ry = listRegion.y + i * LAYER_ROW_H - scroll
      if (ry + LAYER_ROW_H < listRegion.y || ry > listRegion.y + listRegion.h) return
      const action: VrPanelAction = s.id === 'overlay'
        ? { kind: 'layer-overlay', id: o.id }
        : { kind: 'layer-rt', id: o.id }
      p.box(listRegion.x, ry, rowW, LAYER_ROW_H - 8, 14, o.selected ? 'rgba(34,197,94,0.14)' : 'rgba(255,255,255,0.04)', o.selected ? PANEL_COLORS.accent : undefined, 2)
      if (o.selected) p.icon('check', listRegion.x + 36, ry + (LAYER_ROW_H - 8) / 2, 28, PANEL_COLORS.accent)
      // AYNI (owner, 2026-10-20): the stream's kind as a pill at the row's end, in Browse's colours.
      let pillW = 0
      if (o.kind) {
        const colour = KIND_COLOR[o.kindId ?? ''] ?? PANEL_COLORS.textSecondary
        pillW = Math.ceil(p.measure(o.kind, 20, 650)) + 28
        const pillH = 36
        const px = listRegion.x + rowW - 20 - pillW
        const py = ry + (LAYER_ROW_H - 8 - pillH) / 2
        p.box(px, py, pillW, pillH, pillH / 2, 'rgba(255,255,255,0.03)', colour, 1.5)
        p.text(o.kind, px + pillW / 2, py + pillH / 2, { size: 20, weight: 650, color: colour, align: 'center' })
        pillW += 16
      }
      p.text(o.title, listRegion.x + 68, ry + (LAYER_ROW_H - 8) / 2, { size: 26, weight: o.selected ? 650 : 500, maxWidth: rowW - 90 - pillW })
      const top = Math.max(ry, listRegion.y)
      const bottom = Math.min(ry + LAYER_ROW_H - 8, listRegion.y + listRegion.h)
      if (bottom > top) p.hit(listRegion.x, top, rowW, bottom - top, action)
    })
    p.unclip()
    p.scrollbar(listRegion, scroll)
    region = listRegion
    y += listRegion.h + 8
    if (note2) {
      p.text(t('vr.layers.rtTwo'), PAD + 16, y + 20, { size: 22, color: PANEL_COLORS.muted, maxWidth: W - PAD * 2 })
      y += note2
    }
    y += 8
  })
  return region
}

/**
 * AYNI (owner, 2026-10-09): a playing stream's colours, over the Layers
 * page: its palettes as they would draw it, and − / + for its lower and
 * upper limits. Nothing else, as in the browser's dialog. AYNI (owner,
 * 2026-10-20): an Invert checkbox at the end of the palette's label, as
 * the browser's dialog has.
 */
function drawRtColours(p: PanelPainter<VrPanelAction>, rt: VrLayersState['rtPlaying'][number]): void {
  p.button(PAD, 126, 76, 64, '', { kind: 'layer-rt-colours', id: null }, { icon: 'chevron-left', radius: 18 })
  p.box(PAD + 100, 152, 26, 10, 3, '#facc15')
  p.text(rt.title, PAD + 140, 158, { size: 30, weight: 700, maxWidth: W - PAD * 2 - 140 })
  // AYNI (owner, 2026-10-12): its transparency, for every stream; beside Done, as in
  // the browser's dialog (2026-10-20: above Done it lay over the Maximum row).
  transparencyRow(p, H - 122, rt.opacity ?? 1, (dir) => ({ kind: 'rt-opacity', id: rt.id, dir }), PAD, W - PAD * 2 - 240)
  p.button(W - PAD - 220, H - 112, 220, 72, t('palette.dialog.done'), { kind: 'layer-rt-colours', id: null }, { size: 26 })
  const c = rt.colours
  if (!c) {
    note(p, rt.coloursReason ?? '', 236)
    return
  }
  p.text(t('colorbar.palette.label'), PAD, 236, { size: 24, weight: 650, color: PANEL_COLORS.textSecondary })
  const invertLabel = t('palette.dialog.invert')
  const inverted = !!c.inverted
  p.checkbox(W - PAD - 50 - p.measure(invertLabel, 24, 600), 230, inverted, invertLabel, { kind: 'rt-invert', id: rt.id, on: !inverted }, 24, '#facc15')
  const cols = 3
  const gap = 14
  const cellW = (W - PAD * 2 - gap * (cols - 1)) / cols
  const cellH = 96
  const top = 262
  c.palettes.forEach((pal, i) => {
    const x = PAD + (i % cols) * (cellW + gap)
    const y = top + Math.floor(i / cols) * (cellH + gap)
    const active = pal.id === c.palette
    p.box(x, y, cellW, cellH, 16, active ? 'rgba(250,204,21,0.12)' : PANEL_COLORS.surface, active ? '#facc15' : PANEL_COLORS.border, active ? 3 : 1.5)
    if (pal.stops.length > 1) {
      const grad = p.g.createLinearGradient(x + 14, 0, x + cellW - 14, 0)
      pal.stops.forEach((col, k) => grad.addColorStop(k / (pal.stops.length - 1), col))
      p.roundPath(x + 14, y + 14, cellW - 28, 24, 6)
      p.g.fillStyle = grad
      p.g.fill()
    }
    p.text(pal.label, x + 14, y + 68, { size: 24, weight: active ? 700 : 550, color: active ? '#fde68a' : PANEL_COLORS.text, maxWidth: cellW - 28 })
    p.hit(x, y, cellW, cellH, { kind: 'rt-palette', id: rt.id, palette: pal.id })
  })
  let y = top + Math.ceil(c.palettes.length / cols) * (cellH + gap) + 14
  // The ramp as it draws now, between its limits.
  if (c.ramp.length > 1) {
    const grad = p.g.createLinearGradient(PAD, 0, W - PAD, 0)
    c.ramp.forEach((col, k) => grad.addColorStop(k / (c.ramp.length - 1), col))
    p.roundPath(PAD, y, W - PAD * 2, 26, 8)
    p.g.fillStyle = grad
    p.g.fill()
  }
  y += 48
  const limitRow = (label: string, value: string, end: 'min' | 'max', at: { down: boolean; up: boolean }): void => {
    p.box(PAD, y, W - PAD * 2, 92, 18, PANEL_COLORS.surface)
    p.text(label, PAD + 28, y + 46, { size: 26, weight: 600, color: PANEL_COLORS.textSecondary })
    p.text(value, PAD + 300, y + 46, { size: 30, weight: 700, maxWidth: W - PAD * 2 - 300 - 200 })
    p.button(W - PAD - 190, y + 12, 84, 68, '', { kind: 'rt-limit', id: rt.id, end, dir: -1 }, { icon: 'minus', disabled: at.down, radius: 16 })
    p.button(W - PAD - 96, y + 12, 84, 68, '', { kind: 'rt-limit', id: rt.id, end, dir: 1 }, { icon: 'plus', disabled: at.up, radius: 16 })
    y += 106
  }
  limitRow(t('palette.dialog.min'), c.minLabel, 'min', c.atMin)
  limitRow(t('palette.dialog.max'), c.maxLabel, 'max', c.atMax)
}

/**
 * AYNI (owner, 2026-10-12): a layer's transparency, as the browser's
 * slider says it (0 % opaque … 100 % transparent), with − / + a step
 * each (a slider is hard to hold still with a controller's ray).
 */
function transparencyRow(p: PanelPainter<VrPanelAction>, y: number, opacity: number, action: (dir: -1 | 1) => VrPanelAction, x = PAD, w = W - PAD * 2): void {
  const percent = Math.round((1 - Math.min(1, Math.max(0, opacity))) * 100)
  p.box(x, y, w, 92, 18, PANEL_COLORS.surface)
  p.text(t('palette.dialog.transparency'), x + 28, y + 46, { size: 26, weight: 600, color: PANEL_COLORS.textSecondary, maxWidth: w - 28 - 200 - 140 })
  p.text(formatTransparency(percent), x + w - 200 - 24, y + 46, { size: 30, weight: 700, align: 'right' })
  p.button(x + w - 190, y + 12, 84, 68, '', action(-1), { icon: 'minus', disabled: percent <= 0, radius: 16 })
  p.button(x + w - 96, y + 12, 84, 68, '', action(1), { icon: 'plus', disabled: percent >= 100, radius: 16 })
}

// --- View -------------------------------------------------------------

export function drawView(p: PanelPainter<VrPanelAction>, state: VrViewState | null): void {
  header(p, t('vr.view.title'))
  if (!state || !state.hasScale) {
    note(p, t('vr.view.picture'), 150)
    // AYNI (owner, 2026-10-12): a picture's transparency too.
    if (state && typeof state.opacity === 'number') transparencyRow(p, 236, state.opacity, (dir) => ({ kind: 'base-opacity', dir }))
    if (state?.legend) drawParkedLegend(p, state.legend, !!state.legendParked, 350, 350 + LEGEND_BOX_H)
    return
  }
  p.text(t('tools.palette.title'), PAD, 150, { size: 26, weight: 650, color: PANEL_COLORS.textSecondary })
  const cols = 3
  const gap = 16
  const cellW = (W - PAD * 2 - gap * (cols - 1)) / cols
  const cellH = 104
  let y = 182
  state.palettes.forEach((pal, i) => {
    const col = i % cols
    const row = Math.floor(i / cols)
    const x = PAD + col * (cellW + gap)
    const cy = y + row * (cellH + gap)
    const active = pal.id === state.palette
    p.box(x, cy, cellW, cellH, 18, active ? 'rgba(34,197,94,0.12)' : PANEL_COLORS.surface, active ? PANEL_COLORS.accent : PANEL_COLORS.border, active ? 3 : 1.5)
    // The palette's own ramp, as the legend will draw it.
    if (pal.stops.length > 1) {
      const grad = p.g.createLinearGradient(x + 16, 0, x + cellW - 16, 0)
      pal.stops.forEach((c, k) => grad.addColorStop(k / (pal.stops.length - 1), c))
      p.roundPath(x + 16, cy + 16, cellW - 32, 30, 8)
      p.g.fillStyle = grad
      p.g.fill()
    }
    p.text(pal.label, x + 16, cy + 76, { size: 26, weight: active ? 700 : 550, color: active ? PANEL_COLORS.accentText : PANEL_COLORS.text, maxWidth: cellW - 32 })
    p.hit(x, cy, cellW, cellH, { kind: 'palette', id: pal.id })
  })
  const rows = Math.ceil(state.palettes.length / cols)
  y += rows * (cellH + gap) + 16
  // AYNI (owner, 2026-10-14): a dataset with reference lines shares this row with their switch.
  const refs = typeof state.references === 'boolean'
  const rowW = refs ? (W - PAD * 2 - 16) / 2 : W - PAD * 2
  p.box(PAD, y, rowW, 92, 18, PANEL_COLORS.surface)
  p.text(t('tools.palette.invert'), PAD + 28, y + 46, { size: 28, weight: 600, maxWidth: rowW - 28 - 130 })
  p.toggle(PAD + rowW - 112, y + 23, state.inverted, { kind: 'invert' })
  if (refs) {
    const x = PAD + rowW + 16
    p.box(x, y, rowW, 92, 18, PANEL_COLORS.surface)
    p.text(t('reference.toggle'), x + 28, y + 46, { size: 28, weight: 600, maxWidth: rowW - 28 - 130 })
    p.toggle(x + rowW - 112, y + 23, !!state.references, { kind: 'references' })
  }
  y += 112
  p.button(PAD, y, 300, 72, t('colorbar.reset'), { kind: 'reset-view' }, { disabled: state.isDefault, size: 26 })
  // AYNI (owner, 2026-10-12): the dataset's transparency, on Reset's line.
  if (typeof state.opacity === 'number') transparencyRow(p, y - 10, state.opacity, (dir) => ({ kind: 'base-opacity', dir }), PAD + 320, W - PAD * 2 - 320)
  if (state.legend) drawParkedLegend(p, state.legend, !!state.legendParked, y + 92, H - 84)
  p.text(t('vr.view.limitsNote'), PAD, H - 60, { size: 22, color: PANEL_COLORS.muted, maxWidth: W - PAD * 2 })
}

/** The legend's place on the View page, about as tall as the room under Reset. */
const LEGEND_BOX_H = 150

/**
 * AYNI (owner, 2026-10-11): the legend parked on the View page — its
 * title, its bar (or classes) and "Beside the globe" — or, while it is
 * beside the globe, a dashed place saying so with "Park it here".
 */
function drawParkedLegend(p: PanelPainter<VrPanelAction>, legend: VrLegend, parked: boolean, top: number, bottom: number): void {
  const h = bottom - top
  const btnW = 300
  const btnX = W - PAD - 24 - btnW
  const btnY = top + (h - 72) / 2
  const inner = btnX - PAD - 24 - 24
  if (!parked) {
    p.g.save()
    p.g.setLineDash([12, 10])
    p.box(PAD, top, W - PAD * 2, h, 18, 'rgba(255,255,255,0.02)', PANEL_COLORS.border, 2)
    p.g.restore()
    const lines = p.wrap(t('legend.dock.awayVr'), inner, 2, 26)
    lines.forEach((line, i) => p.text(line, PAD + 24, top + h / 2 + (i - (lines.length - 1) / 2) * 34, { size: 26, color: PANEL_COLORS.textSecondary }))
    p.button(btnX, btnY, btnW, 72, t('legend.dock.park'), { kind: 'legend-park', parked: true }, { size: 24 })
    return
  }
  p.box(PAD, top, W - PAD * 2, h, 18, PANEL_COLORS.surface, PANEL_COLORS.accent, 2)
  const x = PAD + 24
  p.text(legend.title + (legend.units ? ` (${legend.units})` : ''), x, top + 30, { size: 24, weight: 650, maxWidth: inner })
  if (legend.kind === 'classes') {
    // The classes in a row, as many as fit.
    let cx = x
    let cy = top + 76
    for (const c of legend.classes) {
      const w = 36 + p.measure(c.label, 22) + 24
      if (cx + w > x + inner) {
        cx = x
        cy += 40
        if (cy > bottom - 20) break
      }
      p.box(cx, cy - 12, 24, 24, 6, c.color)
      p.text(c.label, cx + 34, cy, { size: 22, maxWidth: inner - 34 })
      cx += w
    }
  } else {
    const barTop = top + 58
    const barH = 30
    const grad = p.g.createLinearGradient(x, 0, x + inner, 0)
    legend.stops.forEach((c, i) => grad.addColorStop(i / Math.max(1, legend.stops.length - 1), c))
    p.roundPath(x, barTop, inner, barH, 8)
    p.g.fillStyle = grad
    p.g.fill()
    p.g.strokeStyle = PANEL_COLORS.border
    p.g.lineWidth = 2
    p.g.stroke()
    for (const tick of legend.ticks) {
      const tx = x + tick.position * inner
      p.g.strokeStyle = PANEL_COLORS.text
      p.g.lineWidth = 2
      p.g.beginPath()
      p.g.moveTo(tx, barTop + barH)
      p.g.lineTo(tx, barTop + barH + 8)
      p.g.stroke()
      const label = tick.label
      const lw = p.measure(label, 22, 600)
      // Held inside the bar's ends.
      const lx = Math.min(Math.max(x, tx - lw / 2), x + inner - lw)
      p.text(label, lx, barTop + barH + 26, { size: 22, weight: 600 })
    }
  }
  p.button(btnX, btnY, btnW, 72, t('legend.dock.unparkVr'), { kind: 'legend-park', parked: false }, { size: 24, primary: true })
}

// --- Data -------------------------------------------------------------

/** AYNI (owner, 2026-10-09): a pin being renamed in the headset, and what has been typed so far. */
export interface VrPinEdit {
  id: string
  text: string
}

/** The rename keyboard: digits, then letters; a hyphen for names such as "Dar es-Salaam". */
const PIN_KEY_ROWS = ['1234567890', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm-'] as const

/**
 * A press on the rename keys. The pencil opens the keyboard with the name
 * the viewer gave (empty for a pin's own name); Done hands back what to
 * save, an empty name giving the pin its own back; Cancel leaves it as it
 * was. A letter starting a word is a capital: these are names of places.
 */
export function reducePinEdit(
  edit: VrPinEdit | null,
  action: { kind: 'pin-rename'; id: string } | { kind: 'pin-key'; key: string },
  state: VrPinsState | null,
): { edit: VrPinEdit | null; commit: { id: string; name: string } | null } {
  if (action.kind === 'pin-rename') {
    const pin = state?.pins.find((x) => x.id === action.id)
    return { edit: pin ? { id: pin.id, text: pin.named ? pin.name : '' } : null, commit: null }
  }
  if (!edit) return { edit: null, commit: null }
  switch (action.key) {
    case 'done': return { edit: null, commit: { id: edit.id, name: edit.text.trim() } }
    case 'cancel': return { edit: null, commit: null }
    case 'clear': return { edit: { ...edit, text: '' }, commit: null }
    case 'back': return { edit: { ...edit, text: edit.text.slice(0, -1) }, commit: null }
  }
  if (edit.text.length >= PIN_NAME_MAX) return { edit, commit: null }
  const start = edit.text === '' || edit.text.endsWith(' ')
  if (action.key === 'space') return { edit: start ? edit : { ...edit, text: edit.text + ' ' }, commit: null }
  const ch = action.key.length === 1 ? (start ? action.key.toUpperCase() : action.key) : ''
  return { edit: ch ? { ...edit, text: edit.text + ch } : edit, commit: null }
}

/** The Data page while a pin is renamed: its name as typed, and the keys. */
function drawPinRename(p: PanelPainter<VrPanelAction>, pin: VrPinsState['pins'][number], edit: VrPinEdit): void {
  header(p, t('vr.data.renameTitle'))
  let y = 140
  p.g.beginPath()
  p.g.arc(PAD + 18, y + 20, 14, 0, Math.PI * 2)
  p.g.fillStyle = pin.color
  p.g.fill()
  p.text(t('vr.data.renameHint', { own: pin.own }), PAD + 46, y + 20, { size: 24, color: PANEL_COLORS.textSecondary, maxWidth: W - PAD * 2 - 46 })
  y += 56
  // The field: what has been typed, or the pin's own name greyed while empty.
  const fieldW = W - PAD * 2
  p.box(PAD, y, fieldW, 76, 18, 'rgba(34,197,94,0.1)', PANEL_COLORS.accent, 2)
  p.text(edit.text ? `${edit.text}|` : pin.own, PAD + 26, y + 39, {
    size: 32,
    weight: edit.text ? 650 : 500,
    color: edit.text ? PANEL_COLORS.text : PANEL_COLORS.muted,
    maxWidth: fieldW - 130,
  })
  if (edit.text) p.button(W - PAD - 92, y + 10, 84, 56, '', { kind: 'pin-key', key: 'clear' }, { icon: 'close', radius: 14 })
  y += 100
  const keyW = 92
  const keyH = 68
  const gap = 10
  for (const row of PIN_KEY_ROWS) {
    const rowW = row.length * keyW + (row.length - 1) * gap
    let x = (W - rowW) / 2
    for (const ch of row) {
      p.button(x, y, keyW, keyH, ch.toUpperCase(), { kind: 'pin-key', key: ch }, { size: 30, radius: 12 })
      x += keyW + gap
    }
    y += keyH + gap
  }
  const controls: Array<{ key: string; label: string; w: number; primary?: boolean; icon?: 'backspace' }> = [
    { key: 'space', label: t('vr.browse.keySpace'), w: 320 },
    { key: 'back', label: '', w: 130, icon: 'backspace' },
    { key: 'cancel', label: t('vr.data.renameCancel'), w: 240 },
    { key: 'done', label: t('vr.browse.keyDone'), w: 240, primary: true },
  ]
  const ctrlW = controls.reduce((sum, c) => sum + c.w, 0) + gap * (controls.length - 1)
  let x = (W - ctrlW) / 2
  for (const c of controls) {
    p.button(x, y, c.w, keyH, c.label, { kind: 'pin-key', key: c.key }, { size: 26, radius: 12, primary: c.primary, icon: c.icon })
    x += c.w + gap
  }
}

export function drawData(p: PanelPainter<VrPanelAction>, state: VrPinsState | null, edit: VrPinEdit | null = null): void {
  const editing = edit && state?.readable ? state.pins.find((pin) => pin.id === edit.id) : undefined
  if (editing && edit) {
    drawPinRename(p, editing, edit)
    return
  }
  header(p, t('vr.data.title'))
  if (!state || !state.readable) {
    const reason = state?.reason === 'plan'
      ? t('vr.data.plan')
      : state?.reason === 'picture'
        ? t('vr.data.picture')
        : t('vr.data.none')
    if (state?.reason === 'plan') {
      p.box(PAD, 140, 72, 72, 36, PANEL_COLORS.surface)
      p.icon('lock', PAD + 36, 176, 34, PANEL_COLORS.text)
      const lines = p.wrap(reason, W - PAD * 2 - 100, 4, 28)
      lines.forEach((line, i) => p.text(line, PAD + 100, 160 + i * 40, { size: 28, color: PANEL_COLORS.textSecondary }))
    } else {
      note(p, reason, 150)
    }
    return
  }

  // A or X while aiming at the globe drops a pin (owner, 2026-10-04: the
  // trigger turns the globe, so pins have a button of their own).
  let y = 130
  p.box(PAD, y, W - PAD * 2, 112, 20, 'rgba(21,128,61,0.22)', PANEL_COLORS.accent, 2)
  p.box(PAD + 22, y + 26, 60, 60, 30, PANEL_COLORS.accentDark)
  p.text('A', PAD + 52, y + 57, { size: 30, weight: 800, color: '#ffffff', align: 'center' }) // i18n-exempt: the controller button's own name
  p.text(t('vr.data.pinTitle'), PAD + 104, y + 38, { size: 30, weight: 700 })
  p.text(
    state.full
      ? state.max < 4 ? t('vr.data.fullPlan', { count: state.max }) : t('vr.data.full')
      : t('vr.data.pinHint', { count: state.pins.length, max: state.max }),
    PAD + 104, y + 78, { size: 24, color: PANEL_COLORS.textSecondary, maxWidth: W - PAD * 2 - 130 },
  )
  y += 132

  // The pins. AYNI (2026-10-09): past four (six at most) the rows close
  // up, so the graph keeps its room.
  const compact = state.pins.length > 4
  const rowH = compact ? 56 : 72
  for (const pin of state.pins) {
    const mid = rowH / 2 - 4
    p.g.beginPath()
    p.g.arc(PAD + 22, y + mid, compact ? 11 : 13, 0, Math.PI * 2)
    p.g.fillStyle = pin.color
    p.g.fill()
    p.text(pin.name, PAD + 52, y + (compact ? 16 : 20), { size: compact ? 24 : 26, weight: 650, maxWidth: 400 })
    p.text(pin.where, PAD + 52, y + (compact ? 41 : 50), { size: compact ? 20 : 22, color: PANEL_COLORS.muted, maxWidth: 400 })
    p.text(pin.value, W - PAD - 206, y + mid, { size: compact ? 28 : 30, weight: 700, align: 'right', maxWidth: 300 })
    // AYNI (owner, 2026-10-09): the pencil renames it, on keys drawn on this page.
    p.button(W - PAD - 180, y + 4, 84, rowH - 16, '', { kind: 'pin-rename', id: pin.id }, { icon: 'pencil', radius: 14 })
    p.button(W - PAD - 84, y + 4, 84, rowH - 16, '', { kind: 'pin-remove', id: pin.id }, { icon: 'close', radius: 14 })
    y += rowH
  }
  if (!state.pins.length) {
    p.text(t('vr.data.noPins'), PAD, y + 24, { size: 26, color: PANEL_COLORS.muted })
    y += 60
  }

  // The graph: one line per pin, the playhead in green.
  const chart = state.chart
  const gTop = y + 12
  // AYNI (owner, 2026-10-18): the follow and track switches take a row above the actions.
  const follow = state.follow ?? null
  // AYNI (owner, 2026-10-18): and the choice of where to look, above them.
  const surface = state.surface ?? null
  const gBottom = H - 150 - (follow ? 96 : 0) - (surface ? 80 : 0)
  if (chart && gBottom - gTop > 120) {
    const left = PAD + 110
    const right = W - PAD
    const top = gTop + 10
    const bottom = gBottom - 40
    p.g.strokeStyle = 'rgba(255,255,255,0.1)'
    p.g.lineWidth = 2
    for (const f of [0, 0.5, 1]) {
      p.g.beginPath()
      p.g.moveTo(left, top + f * (bottom - top))
      p.g.lineTo(right, top + f * (bottom - top))
      p.g.stroke()
    }
    p.text(chart.maxLabel, left - 12, top, { size: 22, color: PANEL_COLORS.muted, align: 'right', maxWidth: 100 })
    p.text(chart.minLabel, left - 12, bottom, { size: 22, color: PANEL_COLORS.muted, align: 'right', maxWidth: 100 })
    p.text(chart.startLabel, left, bottom + 28, { size: 22, color: PANEL_COLORS.muted })
    p.text(chart.endLabel, right, bottom + 28, { size: 22, color: PANEL_COLORS.muted, align: 'right' })
    const toX = (x: number) => left + x * (right - left)
    const toY = (v: number) => top + v * (bottom - top)
    // AYNI (owner, 2026-10-14): reference lines, dashed, named at the right end.
    // Top first; the next one's name goes under its line when they sit close (1.5 and 2 °C).
    const placed = chart.references
      .map((ry, i) => (ry === null ? null : { y: toY(ry), label: chart.referenceLabels?.[i] ?? '' }))
      .filter((r): r is { y: number; label: string } => r !== null)
      .sort((a, b) => a.y - b.y)
    placed.forEach((r, i) => {
      p.g.save()
      p.g.strokeStyle = PANEL_COLORS.textSecondary
      p.g.lineWidth = 3
      p.g.setLineDash([16, 10])
      p.g.beginPath()
      p.g.moveTo(left, r.y)
      p.g.lineTo(right, r.y)
      p.g.stroke()
      p.g.restore()
      const below = (i > 0 && r.y - placed[i - 1].y < 30) || r.y - top < 26
      if (r.label) p.text(r.label, right - 6, below ? r.y + 18 : r.y - 16, { size: 22, weight: 650, color: PANEL_COLORS.textSecondary, align: 'right' })
    })
    for (const trace of chart.traces) {
      p.g.strokeStyle = trace.color
      p.g.fillStyle = trace.color
      p.g.lineWidth = 4
      for (const seg of trace.segments) {
        p.g.beginPath()
        seg.forEach((pt, i) => (i ? p.g.lineTo(toX(pt.x), toY(pt.y)) : p.g.moveTo(toX(pt.x), toY(pt.y))))
        p.g.stroke()
      }
      for (const dot of trace.dots) {
        p.g.beginPath()
        p.g.arc(toX(dot.x), toY(dot.y), 5, 0, Math.PI * 2)
        p.g.fill()
      }
    }
    if (chart.now !== null) {
      p.g.strokeStyle = PANEL_COLORS.accent
      p.g.lineWidth = 3
      p.g.beginPath()
      p.g.moveTo(toX(chart.now), top - 6)
      p.g.lineTo(toX(chart.now), bottom + 6)
      p.g.stroke()
    }
  } else if (state.pins.length) {
    p.text(t('vr.data.graphWaiting'), PAD, gTop + 30, { size: 24, color: PANEL_COLORS.muted, maxWidth: W - PAD * 2 })
  }

  if (follow) {
    const fy = H - 112 - 96
    const rowW = (W - PAD * 2 - 16) / 2
    p.box(PAD, fy, rowW, 80, 18, PANEL_COLORS.surface)
    p.text(t('tools.data.pins.follow'), PAD + 24, fy + 40, { size: 24, weight: 600, maxWidth: rowW - 24 - 124 })
    p.toggle(PAD + rowW - 108, fy + 17, follow.on, { kind: 'pins-follow' })
    const tx = PAD + rowW + 16
    p.box(tx, fy, rowW, 80, 18, PANEL_COLORS.surface)
    p.text(t('tools.data.pins.track'), tx + 24, fy + 40, { size: 24, weight: 600, maxWidth: rowW - 24 - 124, color: follow.on ? PANEL_COLORS.text : PANEL_COLORS.muted })
    p.toggle(tx + rowW - 108, fy + 17, follow.track, { kind: 'pins-track' }, !follow.on)
  }

  if (surface) {
    const sy = H - 112 - (follow ? 96 : 0) - 80
    const gap = 12
    const bw = (W - PAD * 2 - gap * 2) / 3
    SURFACE_MODES.forEach((mode, i) => {
      p.button(PAD + i * (bw + gap), sy, bw, 64, t(surfaceLabelKey(mode)), { kind: 'pins-surface', mode }, { size: 24, active: surface === mode })
    })
  }

  // Actions.
  const by = H - 112
  p.button(PAD, by, 560, 76, t('tools.data.pins.extremes'), { kind: 'pins-extremes' }, { size: 26 })
  p.button(PAD + 580, by, W - PAD * 2 - 580, 76, t('tools.data.pins.clear'), { kind: 'pins-clear' }, { size: 26, disabled: state.pins.length === 0 })
}

// --- Help -------------------------------------------------------------

/**
 * AYNI (Pico + Wolvic, 2026-11-03): the Help panel's exit line names the
 * button this browser leaves on (the Meta button in Quest Browser, the left
 * menu button in Wolvic, the headset's own button anywhere else).
 */
function exitTextKey(): 'vr.help.exit.text' | 'vr.help.exit.textWolvic' | 'vr.help.exit.textOther' {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent
  const button = xrExitButton(ua)
  if (button === 'meta') return 'vr.help.exit.text'
  if (button === 'wolvic-menu') return 'vr.help.exit.textWolvic'
  return 'vr.help.exit.textOther'
}

export function drawHelp(p: PanelPainter<VrPanelAction>): void {
  header(p, t('vr.help.title'))
  const rows: Array<[string, string]> = [
    [t('vr.help.trigger.key'), t('vr.help.trigger.text')],
    [t('vr.help.twoTriggers.key'), t('vr.help.twoTriggers.text')],
    [t('vr.help.grip.key'), t('vr.help.grip.text')],
    [t('vr.help.thumbstick.key'), t('vr.help.thumbstick.text')],
    [t('vr.help.zoom.key'), t('vr.help.zoom.text')],
    [t('vr.help.tap.key'), t('vr.help.tap.text')],
    [t('vr.help.spin.key'), t('vr.help.spin.text')],
    [t('vr.help.exit.key'), t(exitTextKey())],
  ]
  let y = 150
  for (const [key, text] of rows) {
    // AYNI (2026-10-22): eight rows since the right stick's zoom and the dock's Spin; 88 px each fits above the foot line.
    p.box(PAD, y, W - PAD * 2, 88, 18, PANEL_COLORS.surface)
    p.text(key, PAD + 28, y + 28, { size: 26, weight: 700, color: PANEL_COLORS.accentText, maxWidth: W - PAD * 2 - 56 })
    p.text(text, PAD + 28, y + 62, { size: 24, color: PANEL_COLORS.textSecondary, maxWidth: W - PAD * 2 - 56 })
    y += 96
  }
  p.text(t('vr.help.more'), PAD, H - 60, { size: 22, color: PANEL_COLORS.muted, maxWidth: W - PAD * 2 })
}
